import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Search, RefreshCw, Filter, ChevronUp, ChevronDown, X, Plus, Trash2, Save, Download, Upload } from 'lucide-react';
import { ThemeToggle } from '../../_shared/components';
import { PageHeader } from './PageHeader';

import { EventMemberSidebar } from './EventMemberSidebar';
import { FollowUpDetailPanel } from './FollowUpDetailPanel';
import { SeatingChart } from './seating-chart/SeatingChart';
import { GlideGrid } from './GlideGrid';
import { CampaignInfoPanel } from './CampaignInfoPanel';
import { ColumnPicker } from './ColumnPicker';
import { FilterBar } from './FilterBar';
import { resolveColumns, type SortState, type FilterRule } from './grid-columns';
import { applyFilterRules, sortRows, buildCsv, exportFilename } from './grid-data';
import { useLauncherCampaign } from '../hooks/useLauncherCampaign';
import { useEventMembers } from '../hooks/useEventMembers';
import { useEventTables } from '../hooks/useEventTables';
import { useEventViews, useUpdateView, useCreateView, useDeleteView } from '../hooks/useEventViews';
import { useFollowUpData } from '../hooks/useFollowUpData';
import {
  useInitEventMembers,
  usePatchEventMember,
  useAssignSeat,
} from '../hooks/useEventMemberMutation';
import { syncCampaignMembers } from '../api-client';
import { formatEventDate, daysUntil } from '../utils/dates';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi, useCurrentUser } from '../../_shared/hooks';
import { Modal } from '../../_shared/components';
import { useSfdcUsers } from '../hooks/useSfdcUsers';
import { addCampaignMember, fetchCampaignMemberStatuses, patchCampaignDetails } from '../event-manager-client';
import type { PatchCampaignFields } from '../event-manager-client';
import { BulkUploadModal } from './BulkUploadModal';
import { CampaignDetailSidebar } from './CampaignDetailSidebar';
import type { EventMember, FollowUpRow } from '../event-manager-client';

// ── Summary card ────────────────────────────────────────────────────────────

function SummaryCard({ label, value, sub }: { label: string; value: string | number; sub?: string }) {
  return (
    <div className="rounded-2xl border border-nightshade-200 dark:border-nightshade-700 bg-white dark:bg-nightshade-900 px-4 py-3">
      <p className="text-[11px] font-mono uppercase tracking-wider font-medium text-nightshade-400 dark:text-nightshade-500">{label}</p>
      <p className="text-2xl font-bold text-nightshade-900 dark:text-nightshade-100 mt-0.5">{value}</p>
      {sub && <p className="text-xs text-nightshade-500 dark:text-nightshade-400 mt-0.5">{sub}</p>}
    </div>
  );
}

// ── Error toast ─────────────────────────────────────────────────────────────

function useErrorToast() {
  const [message, setMessage] = useState<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const show = useCallback((msg: string) => {
    setMessage(msg);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setMessage(null), 4000);
  }, []);

  const Toast = message ? (
    <div className="fixed bottom-4 right-4 z-50 max-w-sm bg-pomegranate-600 text-white text-sm px-4 py-2.5 rounded-lg shadow-lg">
      {message}
    </div>
  ) : null;

  return { show, Toast };
}

// ── Row height toggle ───────────────────────────────────────────────────────

type RowSize = 'S' | 'M' | 'L';
const ROW_HEIGHT_MAP: Record<RowSize, 28 | 34 | 48> = { S: 28, M: 34, L: 48 };

function RowHeightToggle({ size, onChange }: { size: RowSize; onChange: (s: RowSize) => void }) {
  return (
    <div className="flex rounded-full bg-nightshade-100 dark:bg-nightshade-800 p-0.5 text-xs">
      {(['S', 'M', 'L'] as const).map(s => (
        <button
          key={s}
          onClick={() => onChange(s)}
          className={`px-2 py-1 rounded-full transition-colors ${
            size === s
              ? 'bg-nightshade-900 dark:bg-white text-white dark:text-nightshade-900 font-medium'
              : 'text-nightshade-500 dark:text-nightshade-400 hover:text-nightshade-700 dark:hover:text-nightshade-200'
          }`}
        >
          {s}
        </button>
      ))}
    </div>
  );
}

// ── Top-level tabs ──────────────────────────────────────────────────────────

type TopTab = 'attendees' | 'seating';

// ── Main page ───────────────────────────────────────────────────────────────

export function EventManagerPage() {
  const { id } = useParams<{ id: string }>();
  const campaignId = id!;

  const { apiFetch } = useApi();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { isMarketingUser, isAdmin } = useCurrentUser();
  const { campaign } = useLauncherCampaign(campaignId);
  const { members, isLoading: membersLoading } = useEventMembers(campaignId);
  const { tables } = useEventTables(campaignId);
  const initMembers = useInitEventMembers(campaignId);
  const patchMember = usePatchEventMember(campaignId);
  const assignSeat = useAssignSeat(campaignId);
  const { users: sfdcUsers } = useSfdcUsers();

  // ── DB Views ────────────────────────────────────────────────────────────
  const { views, isLoading: viewsLoading } = useEventViews(campaignId);
  const updateViewMutation = useUpdateView(campaignId);
  const createViewMutation = useCreateView(campaignId);
  const deleteViewMutation = useDeleteView(campaignId);

  // SFDC sync — after syncing SFDC data, init event members for any new CMs, then refetch
  const syncMembers = useMutation({
    mutationFn: () => syncCampaignMembers(apiFetch, campaignId),
    onSuccess: () => {
      initMembers.mutate(undefined, {
        onSettled: () => {
          void queryClient.invalidateQueries({ queryKey: ['launcher', 'campaigns', campaignId, 'event-members'] });
        },
      });
    },
  });

  // ── Top-level tab state ─────────────────────────────────────────────────
  const [topTab, setTopTab] = useState<TopTab>('attendees');

  // ── Add member modal state ────────────────────────────────────────────
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [showAddMemberDropdown, setShowAddMemberDropdown] = useState(false);
  const addMemberDropdownRef = useRef<HTMLDivElement>(null);
  const [addMemberForm, setAddMemberForm] = useState({ full_name: '', work_email: '', status: '', notes: '' });
  const addMemberMutation = useMutation({
    mutationFn: (data: { full_name: string; work_email: string; status: string; notes?: string }) =>
      addCampaignMember(apiFetch, campaignId, data),
    onSuccess: () => {
      setShowAddMemberModal(false);
      setAddMemberForm({ full_name: '', work_email: '', status: '', notes: '' });
      syncMembers.mutate();
    },
  });

  // ── Close add-member dropdown on outside click ─────────────────────────
  useEffect(() => {
    if (!showAddMemberDropdown) return;
    const handler = (e: MouseEvent) => {
      if (addMemberDropdownRef.current && !addMemberDropdownRef.current.contains(e.target as Node)) {
        setShowAddMemberDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showAddMemberDropdown]);

  // ── Campaign detail sidebar state ────────────────────────────────────────
  const [showCampaignSidebar, setShowCampaignSidebar] = useState(false);
  const [showEventDetails, setShowEventDetails] = useState(false);
  const patchCampaignMutation = useMutation({
    mutationFn: (fields: PatchCampaignFields) => patchCampaignDetails(apiFetch, campaignId, fields),
    onSuccess: () => {
      setShowCampaignSidebar(false);
      void queryClient.invalidateQueries({ queryKey: ['launcher', 'campaigns', campaignId] });
    },
  });

  // ── Seating chart state (unsaved changes guard) ─────────────────────────
  const [seatingDirty, setSeatingDirty] = useState(false);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [pendingNavTarget, setPendingNavTarget] = useState<TopTab | 'back' | null>(null);

  const handleTabSwitch = useCallback((tab: TopTab) => {
    if (topTab === 'seating' && seatingDirty) {
      setPendingNavTarget(tab);
      setShowUnsavedModal(true);
      return;
    }
    setTopTab(tab);
  }, [topTab, seatingDirty]);

  const handleBackClick = useCallback(() => {
    if (topTab === 'seating' && seatingDirty) {
      setPendingNavTarget('back');
      setShowUnsavedModal(true);
      return;
    }
    navigate('/launcher');
  }, [topTab, seatingDirty, navigate]);

  // ── Active view slug (persisted to localStorage) ──────────────────────
  const [activeSlug, setActiveSlug] = useState<string>(() => {
    try {
      const stored = localStorage.getItem(`event-manager-view-${campaignId}`);
      if (stored) return stored;
    } catch { /* ignore */ }
    return 'all';
  });

  useEffect(() => {
    localStorage.setItem(`event-manager-view-${campaignId}`, activeSlug);
  }, [activeSlug, campaignId]);

  const activeView = useMemo(() => views.find(v => v.slug === activeSlug), [views, activeSlug]);

  // If the stored slug doesn't match any DB view yet (e.g. views still loading), fall back
  const effectiveSlug = activeView ? activeSlug : (views[0]?.slug ?? 'pre-event');
  const effectiveView = activeView ?? views[0] ?? null;

  const isFollowUp = effectiveSlug === 'follow-up';

  // ── Follow-up data (only fetched when follow-up view is active) ──────
  const { rows: followUpRows, isLoading: followUpLoading } = useFollowUpData(campaignId, isFollowUp);

  // ── Row size ──────────────────────────────────────────────────────────
  const [rowSize, setRowSize] = useState<RowSize>(() => {
    try {
      const stored = localStorage.getItem('event-manager-row-height');
      if (stored === 'S' || stored === 'M' || stored === 'L') return stored;
    } catch { /* ignore */ }
    return 'M';
  });

  useEffect(() => {
    localStorage.setItem('event-manager-row-height', rowSize);
  }, [rowSize]);

  // ── Columns derived from active view ────────────────────────────────
  const glideColumns = useMemo(() => {
    if (!effectiveView) return [];
    return resolveColumns(effectiveView.columns);
  }, [effectiveView]);

  // ── Column order, visibility, sort, filters ─────────────────────────
  const [columnOrder, setColumnOrder] = useState<string[]>([]);
  const [hiddenColumns, setHiddenColumns] = useState<Set<string>>(new Set());
  const [sort, setSort] = useState<SortState>({ colIndex: -1, dir: null });
  const [filters, setFilters] = useState<FilterRule[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({});

  // Sync column state from the active view
  const prevViewIdRef = useRef<number | null>(null);
  useEffect(() => {
    if (!effectiveView) return;
    if (prevViewIdRef.current === effectiveView.id) return;
    prevViewIdRef.current = effectiveView.id;
    setColumnOrder(effectiveView.columns);
    setHiddenColumns(new Set());
    setColumnWidths(effectiveView.column_widths ?? {});
    setSort({ colIndex: -1, dir: null });
    // Default filter for Post Event: show only responded members
    if (effectiveView.slug === 'dinner') {
      setFilters([{ id: 'default-responded', field: 'has_responded', operator: 'is_true', value: '' }]);
      setShowFilters(true);
    } else {
      setFilters([]);
      setShowFilters(false);
    }
    setShowSearch(false);
    setSearchValue('');
  }, [effectiveView]);

  const handleColumnWidthChange = useCallback((colId: string, width: number) => {
    setColumnWidths(prev => ({ ...prev, [colId]: width }));
  }, []);

  // Search state
  const [showSearch, setShowSearch] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  // Derive visible columns from order + hidden
  const visibleColumns = useMemo(() => {
    const byId = new Map(glideColumns.map(c => [c.id, c]));
    return columnOrder
      .filter(id => !hiddenColumns.has(id) && byId.has(id))
      .map(id => byId.get(id)!);
  }, [glideColumns, columnOrder, hiddenColumns]);

  const isColumnCustomized = useMemo(() => {
    if (!effectiveView) return false;
    const defaultOrder = effectiveView.columns;
    if (hiddenColumns.size > 0) return true;
    if (columnOrder.length !== defaultOrder.length) return true;
    return columnOrder.some((id, i) => id !== defaultOrder[i]);
  }, [effectiveView, columnOrder, hiddenColumns]);

  const handleColumnReset = useCallback(() => {
    if (!effectiveView) return;
    setColumnOrder(effectiveView.columns);
    setHiddenColumns(new Set());
  }, [effectiveView]);

  // Handle native header drag-reorder
  const handleColumnMoved = useCallback((startIdx: number, endIdx: number) => {
    const visibleIds = columnOrder.filter(id => !hiddenColumns.has(id));
    const movedId = visibleIds[startIdx];
    if (!movedId) return;
    const newVisible = [...visibleIds];
    newVisible.splice(startIdx, 1);
    newVisible.splice(endIdx, 0, movedId);
    const hiddenIds = columnOrder.filter(id => hiddenColumns.has(id));
    setColumnOrder([...newVisible, ...hiddenIds]);
  }, [columnOrder, hiddenColumns]);

  // Sort column name for toolbar pill
  const sortedColumnName = useMemo(() => {
    if (sort.colIndex < 0 || sort.dir === null) return null;
    return visibleColumns[sort.colIndex]?.title ?? null;
  }, [sort, visibleColumns]);

  // ── Save view columns/widths to DB ────────────────────────────────────
  const handleSaveView = useCallback(() => {
    if (!effectiveView) return;
    const visibleIds = columnOrder.filter(id => !hiddenColumns.has(id));
    updateViewMutation.mutate(
      { viewId: effectiveView.id, fields: { columns: visibleIds, column_widths: columnWidths } },
      {
        onSuccess: () => {
          // Update local state to match saved columns so isColumnCustomized resets
          setColumnOrder(visibleIds);
          setHiddenColumns(new Set());
        },
      },
    );
  }, [effectiveView, columnOrder, hiddenColumns, columnWidths, updateViewMutation]);

  // ── Save as new view ──────────────────────────────────────────────────
  const [showSaveAsModal, setShowSaveAsModal] = useState(false);
  const [newViewName, setNewViewName] = useState('');

  const handleSaveAsNewView = useCallback(() => {
    if (!newViewName.trim()) return;
    const visibleIds = columnOrder.filter(id => !hiddenColumns.has(id));
    createViewMutation.mutate(
      { name: newViewName.trim(), columns: visibleIds, column_widths: columnWidths },
      {
        onSuccess: (newView) => {
          setActiveSlug(newView.slug);
          setShowSaveAsModal(false);
          setNewViewName('');
        },
      },
    );
  }, [newViewName, columnOrder, hiddenColumns, columnWidths, createViewMutation]);

  // ── Delete view ───────────────────────────────────────────────────────
  const handleDeleteView = useCallback((viewId: number) => {
    deleteViewMutation.mutate(viewId, {
      onSuccess: () => {
        // Switch to first available view
        const remaining = views.filter(v => v.id !== viewId);
        if (remaining.length > 0) setActiveSlug(remaining[0].slug);
      },
    });
  }, [deleteViewMutation, views]);

  // ── Shared state ────────────────────────────────────────────────────────
  const [selectedMember, setSelectedMember] = useState<EventMember | null>(null);
  const [selectedFollowUp, setSelectedFollowUp] = useState<FollowUpRow | null>(null);
  const { show: showError, Toast } = useErrorToast();

  // Sync SFDC + init event members on mount (uses mutation-level onSuccess which chains initMembers → invalidate)
  const initCalledRef = useRef(false);
  useEffect(() => {
    if (!initCalledRef.current && campaignId) {
      initCalledRef.current = true;
      syncMembers.mutate();
    }
  }, [campaignId]);

  const sfdcStatusesQuery = useQuery({
    queryKey: ['launcher', 'campaigns', campaignId, 'member-statuses'],
    queryFn: () => fetchCampaignMemberStatuses(apiFetch, campaignId),
    staleTime: Infinity,
  });
  const cmStatuses = useMemo(() => {
    if (sfdcStatusesQuery.data?.length) {
      return sfdcStatusesQuery.data.map(s => s.label);
    }
    // Fallback: derive from existing members
    const set = new Set(members.map(m => m.cm_status).filter(Boolean) as string[]);
    return Array.from(set).sort();
  }, [sfdcStatusesQuery.data, members]);

  const resolvedSelected = useMemo(() => {
    if (!selectedMember) return null;
    return members.find(m => m.id === selectedMember.id) ?? null;
  }, [members, selectedMember]);

  const handlePatchMember = useCallback(async (memberId: number, fields: Record<string, unknown>) => {
    await patchMember.mutateAsync({ memberId, fields: fields as any });
    void queryClient.invalidateQueries({ queryKey: ['launcher', 'campaigns', campaignId, 'event-members'] });
  }, [patchMember, queryClient, campaignId]);

  const handleAssignTable = useCallback(async (memberId: number, tableId: number | null) => {
    await assignSeat.mutateAsync({ memberId, tableId });
  }, [assignSeat]);

  const handleAssignTableWithSeat = useCallback(async (memberId: number, tableId: number | null, seatPosition?: number | null) => {
    await assignSeat.mutateAsync({ memberId, tableId, seatPosition });
  }, [assignSeat]);

  // Table name map for glide grid
  const tableMap = useMemo(() => {
    const m = new Map<number, string>();
    for (const t of tables) m.set(t.id, t.table_name);
    return m;
  }, [tables]);

  // Grid data + row click handler depend on active view
  const gridData = isFollowUp ? followUpRows : members;
  const gridLoading = isFollowUp ? followUpLoading : membersLoading;

  const handleGridRowClick = useCallback((row: any) => {
    if (isFollowUp) {
      setSelectedFollowUp(row as FollowUpRow);
      setSelectedMember(null);
    } else {
      setSelectedMember(row as EventMember);
      setSelectedFollowUp(null);
    }
  }, [isFollowUp]);

  // ── CSV Export (every Attendees view) ───────────────────────────────────
  const handleDownloadCsv = useCallback(() => {
    const source: Record<string, any>[] = isFollowUp ? followUpRows : members;
    if (source.length === 0) return;

    const rows = sortRows(applyFilterRules(source, filters), sort, visibleColumns);

    const userNameMap = new Map<string, string>();
    for (const u of sfdcUsers) userNameMap.set(u.userId, u.name);

    const csv = buildCsv(rows, visibleColumns, { tableMap, userNameMap });

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = exportFilename(campaign?.name, effectiveSlug);
    a.click();
    URL.revokeObjectURL(url);
  }, [isFollowUp, followUpRows, members, filters, sort, visibleColumns, sfdcUsers, tableMap, campaign?.name, effectiveSlug]);

  // Find selected row index for highlight
  const selectedRowIndex = useMemo(() => {
    if (isFollowUp) {
      if (!selectedFollowUp) return null;
      const idx = followUpRows.findIndex(r => r.dispatch_log_id === selectedFollowUp.dispatch_log_id);
      return idx >= 0 ? idx : null;
    }
    if (!resolvedSelected) return null;
    const idx = members.findIndex(m => m.id === resolvedSelected.id);
    return idx >= 0 ? idx : null;
  }, [isFollowUp, selectedFollowUp, followUpRows, resolvedSelected, members]);

  // Close sidebar when switching views
  useEffect(() => {
    setSelectedMember(null);
    setSelectedFollowUp(null);
  }, [effectiveSlug]);

  // Summary stats
  const confirmed = useMemo(() => members.filter(m => {
    const s = m.cm_status?.toLowerCase();
    return s === 'attended' || s === 'confirmed';
  }).length, [members]);
  const capacity = campaign?.capacity ?? null;

  const countdown = useMemo(() => {
    const diff = daysUntil(campaign?.start_date);
    if (diff == null) return null;
    if (diff < 0) return 'Event passed';
    if (diff === 0) return 'Today';
    return `${diff}d`;
  }, [campaign?.start_date]);

  const spotsLeft = capacity != null ? Math.max(0, capacity - confirmed) : null;

  const rowCountLabel = isFollowUp
    ? `${followUpRows.length} dispatches`
    : `${members.length} members`;

  return (
    <div className="h-full flex overflow-hidden bg-nightshade-50 dark:bg-nightshade-950">
      <main className="flex-1 min-w-0 overflow-auto">
        <div className="px-6 py-6">
          {/* Page header with back, title, meta, Manager/Launcher tabs */}
          <PageHeader
            campaignId={campaignId}
            campaignName={campaign?.name ?? 'Event Manager'}
            subtype={campaign?.subtype ?? null}
            status={campaign?.status ?? 'Planned'}
            launcherStatus={campaign?.launcher_status ?? 'draft'}
            eventDate={campaign?.start_date ?? campaign?.event_date ?? null}
            city={campaign?.city ?? null}
            currentTab="manager"
            detailsOpen={showEventDetails}
            onToggleDetails={() => setShowEventDetails(v => !v)}
            onEditDetails={() => setShowCampaignSidebar(true)}
          />

          {/* Collapsible event details */}
          {showEventDetails && (
            <div className="mb-4 space-y-4">
              {/* Summary stat cards */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <SummaryCard
                  label="Countdown"
                  value={countdown ?? '\u2014'}
                  sub={formatEventDate(campaign?.start_date, { month: 'short', day: 'numeric' }) ?? undefined}
                />
                <SummaryCard label="Confirmed" value={confirmed} />
                <SummaryCard label="Invited" value={members.length} />
                <SummaryCard label="Spots Left" value={spotsLeft ?? '\u2014'} sub={capacity != null ? `of ${capacity}` : undefined} />
              </div>

              {/* Event info panel */}
              {campaign && (
                <CampaignInfoPanel campaign={campaign} onRefresh={() => syncMembers.mutate()} onEdit={() => setShowCampaignSidebar(true)} />
              )}
            </div>
          )}

          {/* Action bar */}
          <div className="flex items-center justify-between mb-4">
            {/* Sub-tabs: Attendees / Seating */}
            <div className="flex items-center gap-1 border-b border-nightshade-200 dark:border-nightshade-700">
              {([
                ['attendees', 'Attendees'],
                ['seating', 'Seating'],
              ] as const).map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => handleTabSwitch(key)}
                  className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
                    topTab === key
                      ? 'border-blueberry-500 text-blueberry-600 dark:text-blueberry-400'
                      : 'border-transparent text-nightshade-500 dark:text-nightshade-400 hover:text-nightshade-700 dark:hover:text-nightshade-300'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <div className="relative" ref={addMemberDropdownRef}>
                <button
                  onClick={() => setShowAddMemberDropdown(v => !v)}
                  className="flex items-center gap-1.5 h-8 px-3.5 text-xs rounded-full border border-nightshade-200 dark:border-nightshade-700 text-nightshade-600 dark:text-nightshade-300 hover:bg-nightshade-50 dark:hover:bg-nightshade-800 transition-colors"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Add Member
                  <ChevronDown className="w-3 h-3" />
                </button>
                {showAddMemberDropdown && (
                  <div className="absolute right-0 top-full mt-1 w-44 bg-white dark:bg-nightshade-800 border border-nightshade-200 dark:border-nightshade-700 rounded-xl shadow-lg z-20 py-1">
                    <button
                      onClick={() => { setShowAddMemberDropdown(false); setShowAddMemberModal(true); }}
                      className="w-full text-left px-3 py-1.5 text-xs text-nightshade-700 dark:text-nightshade-300 hover:bg-nightshade-50 dark:hover:bg-nightshade-700 flex items-center gap-2"
                    >
                      <Plus className="w-3.5 h-3.5" />
                      Add Single Member
                    </button>
                    <button
                      onClick={() => { setShowAddMemberDropdown(false); setShowBulkUploadModal(true); }}
                      className="w-full text-left px-3 py-1.5 text-xs text-nightshade-700 dark:text-nightshade-300 hover:bg-nightshade-50 dark:hover:bg-nightshade-700 flex items-center gap-2"
                    >
                      <Upload className="w-3.5 h-3.5" />
                      Bulk Upload CSV
                    </button>
                  </div>
                )}
              </div>
              <ThemeToggle />
            </div>
          </div>

          {/* ── Attendees tab (V1 Glide grid) ────────────────────────────── */}
          {topTab === 'attendees' && (
            <>
              {/* Toolbar */}
              <div className="flex items-center gap-3 flex-wrap mb-3">
                {/* View sub-tabs (from DB) */}
                {views.length > 0 && (
                  <div className="flex items-center gap-1 text-xs">
                    {views.map(v => (
                      <button
                        key={v.id}
                        onClick={() => setActiveSlug(v.slug)}
                        className={`px-3 py-1.5 rounded-full transition-colors ${
                          effectiveSlug === v.slug
                            ? 'bg-nightshade-900 dark:bg-white text-white dark:text-nightshade-900 font-medium'
                            : 'text-nightshade-700 dark:text-nightshade-300 font-medium border border-nightshade-200 dark:border-nightshade-600 hover:bg-nightshade-100 dark:hover:bg-nightshade-800'
                        }`}
                      >
                        {v.name}
                      </button>
                    ))}
                  </div>
                )}
                {viewsLoading && (
                  <div className="w-4 h-4 rounded-full border-2 border-blueberry-400 border-t-transparent animate-spin" />
                )}

                <RowHeightToggle size={rowSize} onChange={setRowSize} />

                <ColumnPicker
                  allColumns={glideColumns}
                  columnOrder={columnOrder}
                  hiddenColumns={hiddenColumns}
                  onOrderChange={setColumnOrder}
                  onVisibilityChange={setHiddenColumns}
                  onReset={handleColumnReset}
                  isCustomized={isColumnCustomized}
                />

                {/* Save view button (shows when columns are customized) */}
                {isColumnCustomized && effectiveView && (
                  <>
                    <button
                      onClick={handleSaveView}
                      disabled={updateViewMutation.isPending}
                      className="flex items-center gap-1.5 h-8 px-3.5 text-xs rounded-full border border-blueberry-300 dark:border-blueberry-600 text-blueberry-600 dark:text-blueberry-400 bg-blueberry-50 dark:bg-blueberry-900/20 hover:bg-blueberry-100 dark:hover:bg-blueberry-900/30 transition-colors disabled:opacity-50"
                    >
                      <Save className="w-3.5 h-3.5" />
                      {updateViewMutation.isPending ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      onClick={() => setShowSaveAsModal(true)}
                      className="flex items-center gap-1.5 h-8 px-3.5 text-xs rounded-full border border-nightshade-200 dark:border-nightshade-700 text-nightshade-500 dark:text-nightshade-400 hover:bg-nightshade-50 dark:hover:bg-nightshade-800 transition-colors"
                    >
                      <Plus className="w-3.5 h-3.5" />
                      Save as new view
                    </button>
                  </>
                )}

                {/* Delete custom view */}
                {effectiveView && !effectiveView.is_default && (
                  <button
                    onClick={() => handleDeleteView(effectiveView.id)}
                    disabled={deleteViewMutation.isPending}
                    className="flex items-center gap-1.5 h-8 px-3.5 text-xs rounded-full border border-pomegranate-200 dark:border-pomegranate-800 text-pomegranate-500 hover:bg-pomegranate-50 dark:hover:bg-pomegranate-900/20 transition-colors disabled:opacity-50"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    Delete view
                  </button>
                )}

                {/* Filter toggle */}
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className={`flex items-center gap-1.5 h-8 px-3.5 text-xs rounded-full border transition-colors ${
                    filters.length > 0
                      ? 'border-blueberry-300 dark:border-blueberry-600 text-blueberry-600 dark:text-blueberry-400 bg-blueberry-50 dark:bg-blueberry-900/20'
                      : 'border-nightshade-200 dark:border-nightshade-700 text-nightshade-500 dark:text-nightshade-400 hover:bg-nightshade-50 dark:hover:bg-nightshade-800'
                  }`}
                >
                  <Filter className="w-3.5 h-3.5" />
                  Filter
                  {filters.length > 0 && (
                    <span className="bg-blueberry-500 text-white text-[10px] font-medium rounded-full w-4 h-4 flex items-center justify-center">
                      {filters.length}
                    </span>
                  )}
                </button>

                {/* Search toggle */}
                <button
                  onClick={() => setShowSearch(s => !s)}
                  className={`flex items-center gap-1.5 h-8 px-3.5 text-xs rounded-full border transition-colors ${
                    showSearch
                      ? 'border-blueberry-300 dark:border-blueberry-600 text-blueberry-600 dark:text-blueberry-400 bg-blueberry-50 dark:bg-blueberry-900/20'
                      : 'border-nightshade-200 dark:border-nightshade-700 text-nightshade-500 dark:text-nightshade-400 hover:bg-nightshade-50 dark:hover:bg-nightshade-800'
                  }`}
                >
                  <Search className="w-3.5 h-3.5" />
                  Search
                </button>

                {/* Download CSV (every Attendees view) */}
                <button
                  onClick={handleDownloadCsv}
                  disabled={gridData.length === 0}
                  className="flex items-center gap-1.5 h-8 px-3.5 text-xs rounded-full border border-nightshade-200 dark:border-nightshade-700 text-nightshade-500 dark:text-nightshade-400 hover:bg-nightshade-50 dark:hover:bg-nightshade-800 transition-colors disabled:opacity-50"
                >
                  <Download className="w-3.5 h-3.5" />
                  Download CSV
                </button>

                {/* Sort pill */}
                {sortedColumnName && sort.dir && (
                  <div className="flex items-center gap-1 px-2.5 py-1 text-xs rounded-full bg-nightshade-100 dark:bg-nightshade-800 text-nightshade-700 dark:text-nightshade-300">
                    {sortedColumnName}
                    {sort.dir === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                    <button
                      onClick={() => setSort({ colIndex: -1, dir: null })}
                      className="ml-0.5 text-nightshade-400 hover:text-nightshade-600 dark:hover:text-nightshade-200"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                )}

                {!isFollowUp && (
                  <button
                    onClick={() => syncMembers.mutate()}
                    disabled={syncMembers.isPending}
                    className="flex items-center gap-1.5 h-8 px-3.5 text-xs rounded-full border border-nightshade-200 dark:border-nightshade-700 text-nightshade-500 dark:text-nightshade-400 hover:bg-nightshade-50 dark:hover:bg-nightshade-800 transition-colors disabled:opacity-50"
                  >
                    <RefreshCw className={`w-3.5 h-3.5 ${syncMembers.isPending ? 'animate-spin' : ''}`} />
                    {syncMembers.isPending ? 'Syncing...' : 'Sync SFDC'}
                  </button>
                )}
                <span className="text-xs text-nightshade-400 ml-auto">{rowCountLabel}</span>
              </div>

              {/* Filter bar */}
              {showFilters && (
                <FilterBar
                  filters={filters}
                  onFiltersChange={setFilters}
                  columns={visibleColumns}
                />
              )}

              {/* Glide Grid */}
              {gridLoading ? (
                <div className="flex items-center justify-center py-10">
                  <div className="w-5 h-5 rounded-full border-2 border-blueberry-400 border-t-transparent animate-spin" />
                </div>
              ) : gridData.length === 0 ? (
                <div className="rounded-2xl border border-nightshade-200 dark:border-nightshade-700 py-10 text-center">
                  <p className="text-sm text-nightshade-400 italic">
                    {isFollowUp ? 'No dispatches yet' : 'No event members yet'}
                  </p>
                </div>
              ) : (
                <GlideGrid
                  data={gridData}
                  columns={visibleColumns}
                  rowHeight={ROW_HEIGHT_MAP[rowSize]}
                  onRowClick={handleGridRowClick}
                  selectedRowIndex={selectedRowIndex}
                  sfdcUsers={sfdcUsers}
                  tableMap={tableMap}
                  sort={sort}
                  onSortChange={setSort}
                  filters={filters}
                  columnWidths={columnWidths}
                  onColumnWidthChange={handleColumnWidthChange}
                  onColumnMoved={handleColumnMoved}
                  showSearch={showSearch}
                  searchValue={searchValue}
                  onSearchValueChange={setSearchValue}
                  onSearchClose={() => { setShowSearch(false); setSearchValue(''); }}
                />
              )}
            </>
          )}

          {/* ── Seating tab ──────────────────────────────────────────────── */}
          {topTab === 'seating' && (
            <>
              <div className="flex items-center gap-3 mb-3">
                <button
                  onClick={() => syncMembers.mutate()}
                  disabled={syncMembers.isPending}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border border-nightshade-200 dark:border-nightshade-700 text-nightshade-500 dark:text-nightshade-400 hover:bg-nightshade-50 dark:hover:bg-nightshade-800 transition-colors disabled:opacity-50"
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${syncMembers.isPending ? 'animate-spin' : ''}`} />
                  {syncMembers.isPending ? 'Syncing...' : 'Sync SFDC'}
                </button>
                <span className="text-xs text-nightshade-400 ml-auto">{members.length} members</span>
              </div>

              <SeatingChart
                campaignId={campaignId}
                members={members}
                tables={tables}
                sfdcUsers={sfdcUsers}
                onSelectMember={setSelectedMember}
                onPatchMember={handlePatchMember}
                onAssignTable={handleAssignTableWithSeat}
                showError={showError}
                onDirtyChange={setSeatingDirty}
              />
            </>
          )}

        </div>
      </main>

      {/* Sidebar — follow-up gets read-only panel, others get edit sidebar */}
      {selectedFollowUp && isFollowUp && (
        <FollowUpDetailPanel
          row={selectedFollowUp}
          onClose={() => setSelectedFollowUp(null)}
        />
      )}
      {resolvedSelected && !isFollowUp && (
        <EventMemberSidebar
          member={resolvedSelected}
          tables={tables}
          cmStatuses={cmStatuses}
          sfdcUsers={sfdcUsers}
          onPatchMember={handlePatchMember}
          onAssignTable={handleAssignTable}
          onClose={() => setSelectedMember(null)}
        />
      )}

      {Toast}

      {/* Unsaved seating changes modal */}
      <Modal
        open={showUnsavedModal}
        title="Unsaved Changes"
        onClose={() => setShowUnsavedModal(false)}
        containerClassName="md:!max-w-sm"
        footer={
          <>
            <button
              onClick={() => setShowUnsavedModal(false)}
              className="px-4 py-2 text-sm rounded-full border border-nightshade-200 dark:border-nightshade-600 text-nightshade-700 dark:text-nightshade-300 hover:bg-nightshade-50 dark:hover:bg-nightshade-800"
            >
              Go Back
            </button>
            <button
              onClick={() => {
                setShowUnsavedModal(false);
                setSeatingDirty(false);
                if (pendingNavTarget === 'back') {
                  navigate('/launcher');
                } else if (pendingNavTarget) {
                  setTopTab(pendingNavTarget);
                }
                setPendingNavTarget(null);
              }}
              className="px-4 py-2 text-sm rounded-full bg-pomegranate-500 hover:bg-pomegranate-600 text-white font-medium"
            >
              Discard Changes
            </button>
          </>
        }
      >
        <p className="text-sm text-nightshade-600 dark:text-nightshade-300">
          You have unsaved seating assignments. Leaving this tab will discard your changes.
        </p>
      </Modal>

      {/* Save as new view modal */}
      <Modal
        open={showSaveAsModal}
        title="Save as New View"
        onClose={() => { setShowSaveAsModal(false); setNewViewName(''); }}
        containerClassName="md:!max-w-sm"
      >
        <div className="space-y-3">
          <input
            type="text"
            value={newViewName}
            onChange={e => setNewViewName(e.target.value)}
            placeholder="View name..."
            autoFocus
            className="w-full text-sm bg-white dark:bg-nightshade-800 border border-nightshade-200 dark:border-nightshade-600 rounded-lg px-3 py-2 text-nightshade-700 dark:text-nightshade-300 placeholder:text-nightshade-400 focus:outline-none focus:ring-2 focus:ring-blueberry-400"
            onKeyDown={e => { if (e.key === 'Enter') handleSaveAsNewView(); }}
          />
          <div className="flex gap-2 justify-end">
            <button
              onClick={() => { setShowSaveAsModal(false); setNewViewName(''); }}
              className="px-4 py-1.5 text-sm rounded-full border border-nightshade-200 dark:border-nightshade-600 text-nightshade-600 dark:text-nightshade-300 hover:bg-nightshade-50 dark:hover:bg-nightshade-800 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveAsNewView}
              disabled={!newViewName.trim() || createViewMutation.isPending}
              className="px-4 py-1.5 text-sm rounded-full bg-blueberry-500 text-white hover:bg-blueberry-600 transition-colors disabled:opacity-50"
            >
              {createViewMutation.isPending ? 'Creating...' : 'Create View'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Add member modal */}
      <Modal
        open={showAddMemberModal}
        title="Add Campaign Member"
        onClose={() => { setShowAddMemberModal(false); setAddMemberForm({ full_name: '', work_email: '', status: '', notes: '' }); }}
        containerClassName="md:!max-w-sm"
      >
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-nightshade-600 dark:text-nightshade-300 mb-1">Full Name *</label>
            <input
              type="text"
              value={addMemberForm.full_name}
              onChange={e => setAddMemberForm(f => ({ ...f, full_name: e.target.value }))}
              placeholder="Jane Smith"
              autoFocus
              className="w-full text-sm bg-white dark:bg-nightshade-800 border border-nightshade-200 dark:border-nightshade-600 rounded-lg px-3 py-2 text-nightshade-700 dark:text-nightshade-300 placeholder:text-nightshade-400 focus:outline-none focus:ring-2 focus:ring-blueberry-400"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-nightshade-600 dark:text-nightshade-300 mb-1">Work Email *</label>
            <input
              type="email"
              value={addMemberForm.work_email}
              onChange={e => setAddMemberForm(f => ({ ...f, work_email: e.target.value }))}
              placeholder="jane@company.com"
              className="w-full text-sm bg-white dark:bg-nightshade-800 border border-nightshade-200 dark:border-nightshade-600 rounded-lg px-3 py-2 text-nightshade-700 dark:text-nightshade-300 placeholder:text-nightshade-400 focus:outline-none focus:ring-2 focus:ring-blueberry-400"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-nightshade-600 dark:text-nightshade-300 mb-1">Status</label>
            <select
              value={addMemberForm.status}
              onChange={e => setAddMemberForm(f => ({ ...f, status: e.target.value }))}
              className="w-full text-sm bg-white dark:bg-nightshade-800 border border-nightshade-200 dark:border-nightshade-600 rounded-lg px-3 py-2 text-nightshade-700 dark:text-nightshade-300 focus:outline-none focus:ring-2 focus:ring-blueberry-400"
            >
              <option value="">Select status...</option>
              {cmStatuses.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-nightshade-600 dark:text-nightshade-300 mb-1">Notes</label>
            <textarea
              value={addMemberForm.notes}
              onChange={e => setAddMemberForm(f => ({ ...f, notes: e.target.value }))}
              placeholder="Optional pre-event notes..."
              rows={2}
              className="w-full text-sm bg-white dark:bg-nightshade-800 border border-nightshade-200 dark:border-nightshade-600 rounded-lg px-3 py-2 text-nightshade-700 dark:text-nightshade-300 placeholder:text-nightshade-400 focus:outline-none focus:ring-2 focus:ring-blueberry-400 resize-y"
            />
          </div>
          <div className="flex gap-2 justify-end pt-1">
            <button
              onClick={() => { setShowAddMemberModal(false); setAddMemberForm({ full_name: '', work_email: '', status: '', notes: '' }); }}
              className="px-4 py-1.5 text-sm rounded-full border border-nightshade-200 dark:border-nightshade-600 text-nightshade-600 dark:text-nightshade-300 hover:bg-nightshade-50 dark:hover:bg-nightshade-800 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                if (!addMemberForm.full_name.trim() || !addMemberForm.work_email.trim()) return;
                addMemberMutation.mutate({
                  full_name: addMemberForm.full_name.trim(),
                  work_email: addMemberForm.work_email.trim(),
                  status: addMemberForm.status || 'Invited',
                  ...(addMemberForm.notes.trim() ? { notes: addMemberForm.notes.trim() } : {}),
                });
              }}
              disabled={!addMemberForm.full_name.trim() || !addMemberForm.work_email.trim() || addMemberMutation.isPending}
              className="px-4 py-1.5 text-sm rounded-full bg-blueberry-500 text-white hover:bg-blueberry-600 transition-colors disabled:opacity-50"
            >
              {addMemberMutation.isPending ? 'Adding...' : 'Add to Campaign'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Bulk upload modal */}
      <BulkUploadModal
        open={showBulkUploadModal}
        onClose={() => setShowBulkUploadModal(false)}
        campaignId={campaignId}
        apiFetch={apiFetch}
        cmStatuses={cmStatuses}
        onRefreshStatuses={() => void sfdcStatusesQuery.refetch()}
        onComplete={() => syncMembers.mutate()}
      />

      {/* Campaign detail sidebar */}
      {showCampaignSidebar && campaign && (
        <CampaignDetailSidebar
          campaign={campaign}
          onClose={() => setShowCampaignSidebar(false)}
          onSave={async (fields) => { await patchCampaignMutation.mutateAsync(fields); }}
          saving={patchCampaignMutation.isPending}
          readOnly={!(isMarketingUser || isAdmin)}
        />
      )}
    </div>
  );
}
